import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../lib/translations';
import { LanguageSelector } from '../components/LanguageSelector';

const features = [
  { icon: '✨', titleKey: 'featureAffirmationsTitle', descKey: 'featureAffirmationsDesc' },
  { icon: '🎙️', titleKey: 'featureRecordingTitle', descKey: 'featureRecordingDesc' },
  { icon: '🔥', titleKey: 'featureStreaksTitle', descKey: 'featureStreaksDesc' },
  { icon: '🏆', titleKey: 'featureLeaderboardTitle', descKey: 'featureLeaderboardDesc' },
  { icon: '🔗', titleKey: 'featureMagicLinksTitle', descKey: 'featureMagicLinksDesc' },
  { icon: '🌍', titleKey: 'featureBilingualTitle', descKey: 'featureBilingualDesc' },
] as const;

const steps = [
  { number: '1', titleKey: 'step1Title', descKey: 'step1Desc', icon: '👨‍👩‍👧‍👦' },
  { number: '2', titleKey: 'step2Title', descKey: 'step2Desc', icon: '🎯' },
  { number: '3', titleKey: 'step3Title', descKey: 'step3Desc', icon: '🎤' },
  { number: '4', titleKey: 'step4Title', descKey: 'step4Desc', icon: '📈' },
] as const;

export function Landing() {
  const { language } = useLanguage();

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '1rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        zIndex: 100,
        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      }}>
        <div style={{ fontFamily: 'Quicksand, sans-serif', fontWeight: 700, fontSize: '1.5rem', color: 'var(--primary)' }}>
          MindsetFamily
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/research" style={{ color: 'var(--gray-600)', fontWeight: 500, textDecoration: 'none' }}>
            {t('research', language)}
          </Link>
          <LanguageSelector />
          <Link to="/login" className="btn btn-secondary" style={{ padding: '0.5rem 1rem' }}>
            {t('signIn', language)}
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '6rem 2rem 4rem',
      }}>
        <div style={{ maxWidth: '800px' }}>
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 700,
            color: 'var(--gray-900)',
            marginBottom: '1.5rem',
            lineHeight: 1.2,
          }}>
            {t('landingHero', language)}
          </h1>
          <p style={{
            fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
            color: 'var(--gray-600)',
            marginBottom: '2.5rem',
            lineHeight: 1.6,
          }}>
            {t('landingHeroSubtitle', language)}
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
            <Link to="/signup" className="btn btn-primary btn-large" style={{ fontSize: '1.25rem', padding: '1rem 2.5rem' }}>
              {t('getStarted', language)}
            </Link>
            <p style={{ color: 'var(--gray-500)' }}>
              {t('alreadyHaveAccount', language)}{' '}
              <Link to="/login" style={{ color: 'var(--primary)', fontWeight: 600 }}>
                {t('signIn', language)}
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section style={{
        padding: '5rem 2rem',
        background: 'white',
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '3rem',
            color: 'var(--gray-900)',
          }}>
            {t('featuresTitle', language)}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2rem',
          }}>
            {features.map((feature) => (
              <div
                key={feature.titleKey}
                className="card"
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
              >
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{feature.icon}</div>
                <h3 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--gray-900)' }}>
                  {t(feature.titleKey, language)}
                </h3>
                <p style={{ color: 'var(--gray-600)', lineHeight: 1.6 }}>
                  {t(feature.descKey, language)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section style={{
        padding: '5rem 2rem',
        background: 'linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)',
      }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(2rem, 4vw, 2.5rem)',
            fontWeight: 700,
            textAlign: 'center',
            marginBottom: '3rem',
            color: 'var(--gray-900)',
          }}>
            {t('howItWorksTitle', language)}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '2rem',
          }}>
            {steps.map((step) => (
              <div
                key={step.titleKey}
                style={{
                  textAlign: 'center',
                  padding: '1.5rem',
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  fontSize: '2.5rem',
                  boxShadow: '0 4px 15px rgba(99, 102, 241, 0.15)',
                }}>
                  {step.icon}
                </div>
                <div style={{
                  display: 'inline-block',
                  background: 'var(--primary)',
                  color: 'white',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  lineHeight: '32px',
                  fontWeight: 700,
                  marginBottom: '0.75rem',
                }}>
                  {step.number}
                </div>
                <h3 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--gray-900)' }}>
                  {t(step.titleKey, language)}
                </h3>
                <p style={{ color: 'var(--gray-600)', fontSize: '0.95rem', lineHeight: 1.5 }}>
                  {t(step.descKey, language)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Research Section */}
      <section style={{
        padding: '5rem 2rem',
        background: 'white',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', textAlign: 'center' }}>
          <div style={{ fontSize: '4rem', marginBottom: '1.5rem' }}>🔬</div>
          <h2 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: 700,
            marginBottom: '1rem',
            color: 'var(--gray-900)',
          }}>
            {t('researchCta', language)}
          </h2>
          <p style={{
            fontSize: '1.25rem',
            color: 'var(--gray-600)',
            marginBottom: '2rem',
            lineHeight: 1.6,
          }}>
            {t('researchCtaDesc', language)}
          </p>
          <Link
            to="/research"
            className="btn btn-secondary"
            style={{
              fontSize: '1.125rem',
              padding: '1rem 2rem',
            }}
          >
            {t('research', language)} →
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '5rem 2rem',
        background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <h2 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: 700,
            color: 'white',
            marginBottom: '1rem',
          }}>
            {t('ctaTitle', language)}
          </h2>
          <p style={{
            fontSize: '1.25rem',
            color: 'rgba(255, 255, 255, 0.9)',
            marginBottom: '2rem',
          }}>
            {t('ctaSubtitle', language)}
          </p>
          <Link
            to="/signup"
            className="btn"
            style={{
              background: 'white',
              color: 'var(--primary)',
              fontSize: '1.125rem',
              padding: '1rem 2rem',
              fontWeight: 600,
            }}
          >
            {t('startJourney', language)}
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '2rem',
        background: 'var(--gray-900)',
        textAlign: 'center',
      }}>
        <p style={{ color: 'var(--gray-400)', marginBottom: '0.5rem' }}>
          {t('footerTagline', language)}
        </p>
        <p style={{ color: 'var(--gray-500)', fontSize: '0.875rem' }}>
          © {new Date().getFullYear()} MindsetFamily
        </p>
      </footer>
    </div>
  );
}
